import {
    effect,
    Injectable,
    Injector,
    InputSignal,
    Signal,
    signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import { DatiqGeoLocationProxyService } from '@dat/datiq-geo';
import { isEqual, kebabCase } from 'lodash';
import { firstValueFrom, of } from 'rxjs';
import {
    emptyDatiqGeoSuggestion,
    IDatiqGeoSuggestion,
    IDatiqGeoSuggestionResponse,
} from '../models/iq-geo-suggestion.models';

@Injectable({
    providedIn: 'root',
})
export class IqProxyGeoService {

    geoCache: Map<string, IDatiqGeoSuggestion> = new Map();

    constructor(
        private readonly _geoService: DatiqGeoLocationProxyService,
    ) {}

    resolveGeoSuggestion(
        searchText: string,
    ): Signal<IDatiqGeoSuggestionResponse> {
        const geo: WritableSignal<IDatiqGeoSuggestionResponse> = signal([], {
            equal: isEqual,
        });

        this.fetchSuggestions(searchText).then(
            (result: IDatiqGeoSuggestionResponse) => {
                geo.set(result);
            },
        );

        return geo;
    }

    fetchSuggestions(searchText: string): Promise<IDatiqGeoSuggestionResponse> {
        return firstValueFrom(
            !!searchText && searchText.length >= 3
                ? this._geoService.resolveGeoSuggestion(searchText)
                : of([]),
        );
    }

    getSuggestion(query: string): Signal<IDatiqGeoSuggestion> {
        const geo: WritableSignal<IDatiqGeoSuggestion> = signal(
            emptyDatiqGeoSuggestion,
            {
                equal: isEqual,
            },
        );

        this.fetchFirstResult(query).then((result: IDatiqGeoSuggestion) => {
            geo.set(result);
        });

        return geo;
    }

    fetchFirstResult(query: string): Promise<IDatiqGeoSuggestion> {
        const formattedQuery = kebabCase(query);
        const cached = this.geoCache.get(formattedQuery);
        if (cached) {
            return Promise.resolve(cached);
        }

        const geoPromise = firstValueFrom(
            !!query && query.length > 3
                ? this._geoService.getFirstResult$(query)
                : of(emptyDatiqGeoSuggestion),
        );
        return geoPromise.then((result: IDatiqGeoSuggestion) => {
            this.geoCache.set(formattedQuery, result);
            return result;
        });
    }
}
